import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';

import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';

import { rtlEnable } from '@/domain/env';
import SelectBase from '@/components/mui/Form/Select/SelectBase';
import TextFieldBase from '@/components/mui/Form/TextField/TextFieldBase';
import type { JEIndexFormField } from '@/domain/journal/schemas';

// The cell's own search handler. It reads `currentTarget.value`, so it takes the
// event rather than the value.
export type JECellSearchHandler = (event: React.ChangeEvent<HTMLInputElement>) => void;

// Keyed by field name — the set is decided by the read, so it cannot be spelled
// out. A boolean field holds a boolean; the rest hold what they were seeded
// with, which the field then edits into a string.
export type JEIndexFormValues = Record<string, string | number | boolean>;

// The two field names this form treats specially. Matched by name because the
// read gives no other handle on them.
const NAME_FIELD = 'Name';
const PAYMENT_TERMS_FIELD = 'payment_terms';

// No resolver and no schema: this form has no client-side rules. Submitting an
// empty mandatory field is refused with an error toast, and a second rule here
// would be a second source of truth. `mandatory` still renders its asterisk.
//
// The seed needs normalising for two reasons the legacy form never had to face:
// a text or list field arrives null, and the switch needs a real boolean. The
// legacy form coerced null to '' on the way in, so no field component here has
// ever been asked to handle either.
const seedValue = (field: JEIndexFormField): string | number | boolean =>
  field.type === 'boolean' ? Boolean(field.value) : (field.value ?? '');

const seedValues = (fields: JEIndexFormField[], name: string): JEIndexFormValues => {
  const seeded = fields.reduce<JEIndexFormValues>((acc, field) => ({ ...acc, [field.name]: seedValue(field) }), {});

  return name && !seeded[NAME_FIELD] ? { ...seeded, [NAME_FIELD]: name } : seeded;
};

interface JECellListOptionFormProps {
  fields: JEIndexFormField[];
  // What the user typed into the cell but did not commit; seeds Name when the
  // read supplied none.
  name: string;
  cancel: () => void;
  onSubmit: (values: JEIndexFormValues) => void;
  onSearchHandler: JECellSearchHandler;
}

// Mounted only once the read has answered — see the dialog's spinner branch.
// `defaultValues` are read on the first render and never again, so a form
// mounted before the fields arrive would stay empty for good.
const JECellListOptionForm = ({ fields, name, cancel, onSubmit, onSearchHandler }: JECellListOptionFormProps) => {
  const isRtl = useSelector(rtlEnable);

  const { control, handleSubmit } = useForm<JEIndexFormValues>({ defaultValues: seedValues(fields, name) });

  const renderField = useCallback(
    (field: JEIndexFormField, autoFocus: boolean) => {
      switch (field.type) {
        case 'text':
          return (
            <Controller
              name={field.name}
              control={control}
              render={({ field: rhf }) => (
                <TextFieldBase
                  {...rhf}
                  // MUI switches an input to uncontrolled the moment `value` is
                  // undefined.
                  value={rhf.value ?? ''}
                  onChange={(event) => {
                    rhf.onChange(event);
                    // This field only: the cell underneath shows the name as it
                    // is typed, so its search term follows this input.
                    if (field.name === NAME_FIELD) onSearchHandler?.(event as React.ChangeEvent<HTMLInputElement>);
                  }}
                  variant="outlined"
                  type="text"
                  fullWidth
                  required={field.mandatory}
                  autoFocus={autoFocus}
                  label={field.label}
                  placeholder={field.label}
                  slotProps={{
                    htmlInput:
                      isRtl && field.name === PAYMENT_TERMS_FIELD ? { dir: 'ltr', style: { textAlign: 'right' } } : {},
                  }}
                />
              )}
            />
          );
        case 'list':
          return (
            <Controller
              name={field.name}
              control={control}
              render={({ field: rhf }) => (
                <SelectBase
                  {...rhf}
                  value={rhf.value ?? ''}
                  options={field.options}
                  label={field.label}
                  required={field.mandatory}
                  autoFocus={autoFocus}
                  FormControlProps={{ fullWidth: true }}
                />
              )}
            />
          );
        case 'boolean':
          return (
            <Controller
              name={field.name}
              control={control}
              render={({ field: rhf }) => (
                <FormControlLabel
                  checked={Boolean(rhf.value)}
                  onChange={rhf.onChange}
                  onBlur={rhf.onBlur}
                  control={<Switch size="small" />}
                  label={field.label}
                  sx={{ ml: 0 }}
                />
              )}
            />
          );
        default:
          return null;
      }
    },
    [control, isRtl, onSearchHandler],
  );

  return (
    // noValidate: `mandatory` renders `required` on the input, and the browser's
    // own bubble would fire in its own language with nothing under the field.
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={2} py={1} pb={2}>
        {fields.map((field, index) => (
          <React.Fragment key={field.name}>{renderField(field, index === 0)}</React.Fragment>
        ))}
      </Stack>
      <DialogActions>
        <Button variant="text" onClick={cancel}>
          <FormattedMessage id="components.confirmModal.cancelText" defaultMessage="Cancel" />
        </Button>
        <Button type="submit">
          <FormattedMessage id="components.confirmModal.confirmText" defaultMessage="Confirm" />
        </Button>
      </DialogActions>
    </form>
  );
};

export default JECellListOptionForm;
